import * as React from 'react';
import { connect } from 'react-redux';
import { Translate } from '../../shared/LanguageService';
import { handleError } from '../../shared/ErrorCatching';
import PlanTemplateForm, { PlanGenerationTemplate, SelectOption } from './PlanTemplateForm';
import { Modal, Button } from 'react-bootstrap';
import { create, uploadFiles, clearState, update } from './Actions';
import IPlanTemplateInput from './IPlanTemplateInput';
import IFileUploads from './IFileUploads';
import notify from '../../shared/feedback/notifications';
import ImageModal from './ImageModal';
import IPlanTemplate from './IPlanTemplate';

function transformToSelectOption(
    template: PlanGenerationTemplate | undefined
): SelectOption | null {
    if (!template) {
        return null;
    }

    return {
        value: template.id,
        label: template.name
    };
}

type FileUpload = {
    files: string[];
};

type ReduxProps = {
    planTemplate: IPlanTemplate;
    imageReferences: string[];
    availablePlanGenerationTemplates: PlanGenerationTemplate[];
};

type ReduxActions = {
    actions: {
        create: (input: IPlanTemplateInput, fileUploads: IFileUploads) => void;
        uploadFiles: (id: number, fileUploads: IFileUploads) => void;
        clearState: () => void;
        update: (input: IPlanTemplateInput, fileUploads: IFileUploads) => void;
    };
};

type PlanTemplateModalProps = {
    editId: number;
    shouldShowModal: boolean;
    closeModal: (templateId: number) => void;
    editInput: IPlanTemplate;
} & ReduxProps &
    ReduxActions;

type PlanTemplateModalState = {
    planTemplateName: string;
    planGenerationTemplate: SelectOption;
    isAutoGenerationActive: boolean;
    fileUploads: FileUpload;
    errors: any;
    shouldRemoveTemplateOnClose: boolean;
    planTemplate: IPlanTemplate | {};
    imageReferences: string[];
    selectedImage: string;
    shouldShowImageModal: boolean;
    creating: boolean;
};

export class PlanTemplateModal extends React.Component<
    PlanTemplateModalProps,
    PlanTemplateModalState
> {
    constructor(props: any) {
        super(props);

        const planGenerationTemplate: PlanGenerationTemplate | undefined =
            props.availablePlanGenerationTemplates.find(
                (template) => template.id === props.editInput.planGenerationTemplate
            );

        this.state = {
            planTemplateName: props.editInput.name,
            planGenerationTemplate: transformToSelectOption(planGenerationTemplate),
            isAutoGenerationActive: props.editInput.isAutoGenerationActive,
            fileUploads: { files: [] },
            errors: null,
            shouldRemoveTemplateOnClose: false,
            planTemplate: {},
            imageReferences: [],
            selectedImage: '',
            shouldShowImageModal: false
        };

        this.save = this.save.bind(this);
        this.create = this.create.bind(this);
        this.imageChange = this.imageChange.bind(this);
        this.checkRemoveTemplateOnClose = this.checkRemoveTemplateOnClose.bind(this);
        this.update = this.update.bind(this);
        this.showImageModal = this.showImageModal.bind(this);
        this.closeImageModal = this.closeImageModal.bind(this);
    }

    componentDidMount() {
        this.props.actions.clearState();

        if (this.props.editId > 0) {
            this.setState({
                imageReferences: this.props.editInput.imageReferences
            });
        }
    }

    imageChange(e: any): void {
        e.preventDefault();
        let fileUploads: FileUpload = { files: [] };
        for (let file of e.target.files) {
            let reader = new FileReader();
            reader.onloadend = () => {
                file.data = reader.result;
                fileUploads.files.push(file);
                this.setState({
                    fileUploads: fileUploads,
                    imageReferences: []
                });
            };
            reader.readAsArrayBuffer(file);
        }
    }

    save(): void {
        this.setState({ creating: true });
        if (this.props.editId === -1) {
            this.create();
        } else {
            this.update();
        }
    }

    getPlanTemplateValues = (): IPlanTemplateInput => {
        const { planTemplateName, planGenerationTemplate, isAutoGenerationActive } = this.state;

        return {
            name: planTemplateName,
            planGenerationTemplate: planGenerationTemplate ? planGenerationTemplate.value : null,
            isAutoGenerationActive
        };
    };

    create(): void {
        if (Object.keys(this.props.planTemplate).length !== 0) {
            this.props.actions
                .uploadFiles(this.props.planTemplate.id, this.state.fileUploads)
                .then(() => {
                    if (this.props.imageReferences) {
                        this.setState({ creating: false, errors: [] });
                        this.props.closeModal(0);
                        notify.success(
                            Translate('plan-templates/notification/plan-template-added')
                        );
                    } else {
                        this.setState({
                            creating: false,
                            shouldRemoveTemplateOnClose: true
                        });
                        notify.failure(Translate('plan-templates/notification/error-file-upload'));
                    }
                })
                .catch((error: any) =>
                    handleError(error, (errors: any): void => {
                        this.setState({
                            errors: errors,
                            creating: false,
                            shouldRemoveTemplateOnClose: true
                        });
                    })
                );
        } else {
            const values = this.getPlanTemplateValues();

            this.props.actions
                .create(values, this.state.fileUploads)
                .then(() => {
                    if (this.props.planTemplate.imageReferences) {
                        this.setState({ creating: false, errors: [] });
                        this.props.closeModal(0);
                        notify.success(
                            Translate('plan-templates/notification/plan-template-added')
                        );
                    } else {
                        this.setState({
                            creating: false,
                            shouldRemoveTemplateOnClose: true
                        });
                        notify.failure(Translate('plan-templates/notification/error-file-upload'));
                    }
                })
                .catch((error: any) =>
                    handleError(error, (errors: any): void => {
                        this.setState({
                            errors: errors,
                            creating: false,
                            shouldRemoveTemplateOnClose: true
                        });
                    })
                );
        }
    }

    update(): void {
        let fileUploads;
        if (this.state.imageReferences.length !== 0) {
            fileUploads = { files: [] };
        } else {
            fileUploads = this.state.fileUploads;
        }

        const { actions } = this.props;

        const values = this.getPlanTemplateValues();

        actions
            .update(values, fileUploads, this.props.editId)
            .then(() => {
                this.setState({ creating: false });
                notify.success(Translate('plan-templates/notification/plan-template-updated'));

                this.props.closeModal(0);
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    this.setState({ errors: errors, creating: false });
                })
            );
    }

    checkRemoveTemplateOnClose(): void {
        let planTemplate = this.props.planTemplate;
        let shouldRemoveTemplateOnClose = this.state.shouldRemoveTemplateOnClose;

        if (planTemplate.id && shouldRemoveTemplateOnClose && this.props.editId < 0) {
            this.props.closeModal(planTemplate.id);
        } else {
            this.props.closeModal(0);
        }
    }

    showImageModal(reference: string): void {
        this.setState({ shouldShowImageModal: true, selectedImage: reference });
    }

    closeImageModal(): void {
        this.setState({ shouldShowImageModal: false });
    }

    handlePlanTemplateNameChange = (newValue: string) => {
        this.setState({ planTemplateName: newValue });
    };

    handlePlanGenerationTemplateIdChange = (newValue: SelectOption) => {
        this.setState({ planGenerationTemplate: newValue });
    };

    handleIsAutoGenerationActiveChange = (newValue: boolean) => {
        this.setState({ isAutoGenerationActive: newValue });
    };

    shouldSaveBeDisabled = (): boolean => {
        return (
            this.state.creating ||
            !this.state.planTemplateName ||
            (this.state.fileUploads.files.length === 0 && this.state.imageReferences.length === 0)
        );
    };

    render() {
        const {
            planTemplateName,
            planGenerationTemplate,
            isAutoGenerationActive,
            imageReferences,
            errors
        } = this.state;
        const { availablePlanGenerationTemplates } = this.props;

        return (
            <div>
                <Modal show={this.props.shouldShowModal} onHide={this.checkRemoveTemplateOnClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {this.props.editId > 0
                                ? Translate('plan-templates/modal/title/edit-plan-template')
                                : Translate('plan-templates/modal/title/add-plan-template')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <PlanTemplateForm
                            errors={errors}
                            planTemplateName={planTemplateName}
                            onChangePlanTemplateName={this.handlePlanTemplateNameChange}
                            availablePlanGenerationTemplates={availablePlanGenerationTemplates}
                            planGenerationTemplate={planGenerationTemplate}
                            onChangePlanGenerationTemplate={
                                this.handlePlanGenerationTemplateIdChange
                            }
                            isAutoGenerationActive={isAutoGenerationActive}
                            onChangeIsAutoGenerationActive={this.handleIsAutoGenerationActiveChange}
                            onImageChange={this.imageChange}
                            imageReferences={imageReferences}
                            imageModal={this.showImageModal}
                        />
                        <ImageModal
                            shouldShowImageModal={this.state.shouldShowImageModal}
                            imageReference={this.state.selectedImage}
                            closeImageModal={this.closeImageModal}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => this.checkRemoveTemplateOnClose()}>
                            {Translate('plan-templates/modal/footer-button/close')}
                        </Button>
                        <Button
                            disabled={this.shouldSaveBeDisabled()}
                            bsStyle="primary"
                            onClick={this.save}
                        >
                            {Translate('plan-templates/modal/footer-button/save')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

function mapStateToProps(state: any) {
    return {
        planTemplate: state.planTemplates.create.planTemplate,
        imageReferences: state.planTemplates.create.imageReferences,
        availablePlanGenerationTemplates: state.planTemplates.availablePlanGenerationTemplates
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            create: (
                input: IPlanTemplateInput,
                fileUploads: IFileUploads
            ): Promise<IPlanTemplate> => dispatch(create(input, fileUploads)),
            uploadFiles: (id: number, fileUploads: IFileUploads): Promise<string> =>
                dispatch(uploadFiles(id, fileUploads)),
            clearState: () => {
                dispatch(clearState());
            },
            update: (
                input: IPlanTemplateInput,
                fileUploads: IFileUploads,
                id: number
            ): Promise<IPlanTemplate> => dispatch(update(input, fileUploads, id))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(PlanTemplateModal);
